/**
 * Member Payments tab (DNKADM-0391) — the one place that answers "what has this person
 * paid, and what are they about to be charged". Every operator, STAFF included, has it
 * (DNKADM-0465, DNKAPI-0675); Refund/Void in the history stays OWNER/ADMIN.
 *
 * Two columns on desktop and the front-desk iPad in landscape, stacked on narrow widths
 * with Upcoming on top:
 *  - **Payment history** — the existing {@link MemberTransactionsTab} for this contact,
 *    unchanged (same table, paging, empty/error states, Refund/Void, ledger link).
 *  - **Upcoming** — the membership's next charge: plan name, the next-charge billing
 *    block shared with `MembershipSummaryBar` (DNKADM-0374 — date in words, estimated
 *    amount with its "est." label, never $0.00 for an unpriced plan, pause copy), and the
 *    card that will be charged when the membership read carries it. No membership ⇒
 *    "No upcoming membership charges" and nothing else. Either read failing ⇒ a short
 *    error with Try again; the history column is unaffected.
 *
 * This is a surfacing change: both reads already exist (`getMemberMembership`,
 * `getMembershipNextCharge`), nothing is derived client-side, and the next-charge copy
 * is the extracted {@link NextChargeBillingBlock}, not a copy of it.
 */
import { useCallback } from 'react';
import { Box, Paper, Skeleton, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { getMemberMembership, type MemberMembership } from '@/api/members';
import { getMembershipNextCharge, type MembershipNextCharge } from '@/api/membershipNextCharge';
import { useResource } from '@/hooks/useResource';
import { inkAlpha, resolveTokens } from '@/theme';
import { dedupeCardBrand } from '@/utils/cardBrand';
import { scrubBrand } from '@/utils/whitelabel';
import { MemberTransactionsTab } from './MemberTransactionsTab';
import { NextChargeBillingBlock } from './NextChargeBillingBlock';
import { ScreenError } from '@/components/ScreenError';

/** The right column's width beside the history table (desktop / iPad landscape). */
const UPCOMING_COLUMN_WIDTH = 320;

interface UpcomingRead {
  membership: MemberMembership;
  nextCharge: MembershipNextCharge;
}

/** Both reads in parallel — the card needs the plan name and card from one, the date and amount from the other. */
async function loadUpcoming(contactId: string): Promise<UpcomingRead> {
  const [membership, nextCharge] = await Promise.all([
    getMemberMembership(contactId),
    getMembershipNextCharge(contactId),
  ]);
  return { membership, nextCharge };
}

export function MemberPaymentsTab({ contactId }: { contactId: string }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gap: 2.5,
        alignItems: 'start',
        // Narrow: one column, Upcoming first. Wide (desktop, iPad landscape): history
        // takes the width, Upcoming sits beside it at a fixed narrow width.
        gridTemplateColumns: { xs: '1fr', md: `minmax(0, 1fr) ${UPCOMING_COLUMN_WIDTH}px` },
      }}
    >
      <Box component="section" aria-labelledby="member-payments-upcoming" sx={{ order: { xs: 0, md: 1 } }}>
        <Typography id="member-payments-upcoming" component="h2" variant="h3" sx={{ mb: 1.5 }}>
          Upcoming
        </Typography>
        <UpcomingChargeCard contactId={contactId} />
      </Box>
      <Box component="section" aria-labelledby="member-payments-history" sx={{ order: { xs: 1, md: 0 }, minWidth: 0 }}>
        <Typography id="member-payments-history" component="h2" variant="h3" sx={{ mb: 1.5 }}>
          Payment history
        </Typography>
        <MemberTransactionsTab contactId={contactId} />
      </Box>
    </Box>
  );
}

function UpcomingChargeCard({ contactId }: { contactId: string }) {
  const theme = useTheme();
  const t = resolveTokens(theme);
  const upcoming = useResource(
    useCallback(() => loadUpcoming(contactId), [contactId]),
    [contactId],
  );

  return (
    <Paper elevation={1} sx={{ p: 2.5 }}>
      {upcoming.status === 'loading' && (
        <Box role="status" aria-label="Loading upcoming charge">
          <Skeleton height={24} width="60%" />
          <Skeleton height={20} sx={{ mt: 0.5 }} />
          <Skeleton height={20} width="80%" />
        </Box>
      )}
      {upcoming.status === 'error' && (
        <ScreenError
          variant="panel"
          error={upcoming.error}
          what="the upcoming charge"
          onRetry={upcoming.refetch}
          retryMessage="Payment history is unaffected."
          retryLabel="Try again"
        />
      )}
      {upcoming.status === 'ready' && upcoming.data && (
        <UpcomingChargeBody read={upcoming.data} lineColor={t.lineFaint} />
      )}
    </Paper>
  );
}

function UpcomingChargeBody({ read, lineColor }: { read: UpcomingRead; lineColor: string }) {
  const theme = useTheme();
  const { membership, nextCharge } = read;
  // "Has a membership" means the read names a plan — the same test the summary bar's
  // next-charge fetch keys on. `no_membership` from the next-charge read is the server
  // saying the same thing; either way there is nothing upcoming.
  const hasMembership = membership.membershipPlanId !== null && nextCharge.reason !== 'no_membership';
  if (!hasMembership) {
    return <Typography sx={{ color: inkAlpha(theme, 0.62) }}>No upcoming membership charges</Typography>;
  }

  // `maskedCard` is backend text (DNKAPI-0241): scrubbed at the render boundary and
  // brand-deduped, exactly as the summary bar renders its card line.
  const cardLine = dedupeCardBrand(scrubBrand(membership.maskedCard));
  const isPaused = nextCharge.reason === 'paused' || membership.subscriptionStatus === 'paused';

  // DNKADM-0393 U4 — each label sits tight to its own value and the gap BETWEEN
  // pairs is the larger one, so the card reads as label/value pairs instead of a
  // ladder of evenly-spaced lines. Only the two gaps move: `lineHeight: 1.35` pulls
  // each label onto its value, `spacing={1.75}` + `pt: 1.75` open the seams between
  // pairs, and the next-charge block takes the same tight top gap as the other two
  // labels (its default `0.75` is what made that one pair read as two lines).
  const pairLabelSx = { fontSize: 13, lineHeight: 1.35, color: inkAlpha(theme, 0.62) };

  return (
    <Stack spacing={1.75}>
      <Box>
        <Typography sx={pairLabelSx}>Plan</Typography>
        <Typography sx={{ fontWeight: 600 }}>{membership.membershipPlanName ?? 'Membership'}</Typography>
      </Box>
      <Box sx={{ borderTop: `1px solid ${lineColor}`, pt: 1.75 }}>
        <Typography sx={pairLabelSx}>Next charge</Typography>
        <NextChargeBillingBlock data={nextCharge} theme={theme} topGap={0.25} />
        {isPaused && (
          <Typography sx={{ fontSize: 13, mt: 0.5, color: inkAlpha(theme, 0.62) }}>
            Nothing is charged while the membership is paused.
          </Typography>
        )}
        {!isPaused && nextCharge.pauseEffectiveOn !== null && (
          <Typography sx={{ fontSize: 13, mt: 0.5, color: inkAlpha(theme, 0.62) }}>
            Once the pause starts, no charges are taken until it is resumed.
          </Typography>
        )}
      </Box>
      {cardLine !== null && (
        <Box sx={{ borderTop: `1px solid ${lineColor}`, pt: 1.75 }}>
          <Typography sx={pairLabelSx}>Card to be charged</Typography>
          <Typography>{cardLine}</Typography>
        </Box>
      )}
    </Stack>
  );
}
